<template>
  <div
    class="bg-white rounded-[12px] p-5 shadow-sm border border-[#E5E5E5]"
    data-testid="impact-interface-quality"
  >
    <div class="flex flex-wrap items-center justify-between gap-2 mb-4">
      <div class="flex items-center gap-2 min-w-0">
        <p class="text-[13px] font-medium text-[var(--color-dark)] m-0">
          {{ $t('UserProjects.impactAnalysis.interfaceQuality.title') }}
        </p>
        <span
          class="text-[9px] font-bold px-1.5 py-0.5 rounded-full shrink-0"
          style="background-color: #FEFCE8; color: #EAB308"
        >
          {{ $t('UserProjects.impactAnalysis.interfaceQuality.badge') }}
        </span>
      </div>
      <span class="text-[11px] text-[var(--color-disabled)]">
        {{ $t('UserProjects.impactAnalysis.interfaceQuality.scoreHint') }}
      </span>
    </div>

    <p class="text-[11px] text-[var(--color-disabled)] mb-3 m-0">
      {{ $t('UserProjects.impactAnalysis.interfaceQuality.subtitle') }}
      <span v-if="trendLabel" class="ml-1" :style="{ color: trendColor }">
        {{ trendText }}
      </span>
    </p>

    <div
      v-if="insufficientData || !interfaces.length"
      class="text-[13px] text-[var(--color-disabled)] py-2"
      data-testid="impact-interface-quality-empty"
    >
      {{ $t('UserProjects.impactAnalysis.interfaceQuality.empty') }}
    </div>

    <div v-else class="space-y-2" data-testid="impact-interface-quality-list">
      <div
        v-for="row in interfaces"
        :key="row.key"
        class="flex items-center gap-3 py-1.5 border-b border-[#F0F0F0] last:border-0"
        :data-testid="`impact-interface-quality-row-${row.key}`"
      >
        <span class="text-[12px] text-[var(--color-dark)] w-36 shrink-0 truncate">
          {{ formatPair(row) }}
        </span>
        <div class="flex-1 h-2.5 rounded-full bg-[#F0F0F0] overflow-hidden max-w-[140px]">
          <div
            class="h-full rounded-full"
            :style="{ width: `${row.score}%`, backgroundColor: barColor(row.colorHint) }"
          />
        </div>
        <span
          class="text-[12px] font-bold w-8 text-right"
          :style="{ color: barColor(row.colorHint) }"
        >
          {{ row.score }}
        </span>
        <span class="text-[11px] text-[var(--color-disabled)] w-28 text-right shrink-0">
          {{ openLabel(row.openConstraints) }}
        </span>
      </div>
    </div>

    <div
      v-if="insight && insight.recurringDependency"
      class="mt-3 pt-3 border-t border-[#F0F0F0]"
      data-testid="impact-interface-quality-insight"
    >
      <p class="text-[11px] text-[var(--color-disabled)] m-0 leading-relaxed">
        <span class="font-medium text-[var(--color-dark)]">
          {{ $t('UserProjects.impactAnalysis.interfaceQuality.recurringLabel') }}
        </span>
        {{ formatDependency(insight.recurringDependency) }}.
        <span v-if="insight.affectedCategories?.length" class="font-medium text-[var(--color-dark)]">
          {{ $t('UserProjects.impactAnalysis.interfaceQuality.affectedLabel') }}
        </span>
        <template v-if="insight.affectedCategories?.length">
          {{ formatCategories(insight.affectedCategories) }}.
        </template>
        <template v-if="insight.recommendationKey">
          {{ $t('UserProjects.impactAnalysis.interfaceQuality.recommendationLabel') }}
          {{ recommendationText(insight.recommendationKey) }}
        </template>
      </p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ImpactInterfaceQualityPanel',
  props: {
    interfaceQuality: {
      type: Object,
      default: null,
    },
  },
  computed: {
    insufficientData() {
      return Boolean(this.interfaceQuality?.insufficientData);
    },
    interfaces() {
      return this.interfaceQuality?.interfaces || [];
    },
    insight() {
      return this.interfaceQuality?.insight || null;
    },
    trendLabel() {
      return this.interfaceQuality?.trendLabel || null;
    },
    trendText() {
      if (this.trendLabel === 'worsening') {
        return this.$t('UserProjects.impactAnalysis.interfaceQuality.trendWorsening');
      }
      if (this.trendLabel === 'improving') {
        return this.$t('UserProjects.impactAnalysis.interfaceQuality.trendImproving');
      }
      if (this.trendLabel === 'stable') {
        return this.$t('UserProjects.impactAnalysis.interfaceQuality.trendStable');
      }
      return '';
    },
    trendColor() {
      if (this.trendLabel === 'worsening') return '#EAB308';
      if (this.trendLabel === 'improving') return '#22C55E';
      return '#ABABAB';
    },
  },
  methods: {
    categoryLabel(key) {
      const i18nKey = `UserProjects.impactAnalysis.interfaceQuality.categories.${key}`;
      return this.$te(i18nKey) ? this.$t(i18nKey) : key;
    },
    formatPair(row) {
      const from = this.categoryLabel(row.from);
      const to = this.$t('UserProjects.impactAnalysis.interfaceQuality.crossTarget');
      return `${from} → ${to}`;
    },
    formatDependency(value) {
      if (!value || !value.includes('→')) return value;
      const [fromRaw] = value.split('→').map((part) => part.trim());
      return `${this.categoryLabel(fromRaw)} → ${this.$t('UserProjects.impactAnalysis.interfaceQuality.crossTarget')}`;
    },
    formatCategories(categories) {
      return categories.map((key) => this.categoryLabel(key)).join(', ');
    },
    recommendationText(key) {
      const i18nKey = `UserProjects.impactAnalysis.interfaceQuality.recommendations.${key}`;
      return this.$te(i18nKey) ? this.$t(i18nKey) : '';
    },
    openLabel(count) {
      if (!count) return this.$t('UserProjects.impactAnalysis.interfaceQuality.noBlockades');
      return this.$t('UserProjects.impactAnalysis.interfaceQuality.openConstraints', { count });
    },
    barColor(hint) {
      if (hint === 'green') return '#22C55E';
      if (hint === 'red') return '#EF4444';
      return '#EAB308';
    },
  },
};
</script>
